import { Injectable, inject } from '@angular/core';
import { StateContext, Store } from '@ngxs/store';
import { FeaturePurePipe } from '@wilson/authorization';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import {
  PreferenceSettings,
  PreferencesGatewayService,
} from '@wilson/preferences/core';
import {
  catchError,
  combineLatest,
  EMPTY,
  first,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { captureException } from '@sentry/angular';
import { FetchAndResolveLocationSettingsRemotely } from '../location-preferences/location-preferences.actions';
import { FetchShiftTimePreferenceSettingAction } from '../shift-time-preference-setting/shift-time-preference-setting.actions';
import { FetchUserGeneralPreferences } from '../user-general-preferences/user-general-preferences.actions';
import { FetchSelectedLocation } from '../user-location-preferences/user-location-preferences.actions';
import { FetchUserShiftTimePreferenceAction } from '../user-shift-time-preference/user-shift-time-preference.actions';
import { FetchUserTimePreferences } from '../user-time-preferences/user-time-preferences.actions';
import { FetchSelectedVehicles } from '../user-vehicle-preferences/user-vehicle-preferences.actions';
import { FetchVehiclePreferenceSetting } from '../vehicle-preferences/vehicle-preferences.actions';
import { FetchPreferenceSettings } from './preference-settings.actions';
import {
  PreferenceSettingsState,
  PreferenceSettingsStateModel,
} from './preference-settings.state';
import { FeatureName } from '@wilson/interfaces';

@Injectable({
  providedIn: 'root',
})
export class PreferenceSettingsStateService {
  private store = inject(Store);
  private readonly preferencesGatewayService = inject(
    PreferencesGatewayService,
  );
  private readonly featurePurePipe = inject(FeaturePurePipe);
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);

  checkAndLoadPreferenceData(withSubSettingsFetch = true) {
    this.hasPreferencePermission()
      .pipe(
        take(1),
        switchMap((hasPreferencePermission) => {
          if (hasPreferencePermission) {
            return this.store.dispatch(new FetchPreferenceSettings()).pipe(
              first(),
              tap(() => {
                if (withSubSettingsFetch) {
                  this.triggerFetchForSubSettings(
                    this.store.selectSnapshot(
                      PreferenceSettingsState.preferenceSettingsState,
                    ),
                  );
                }
              }),
            );
          } else {
            return EMPTY;
          }
        }),
        catchError((err) => {
          captureException(err, {
            tags: { source: 'checkAndLoadPreferenceData' },
          });
          return EMPTY;
        }),
      )
      .subscribe();
  }

  hasPreferencePermission() {
    return combineLatest([
      this.featurePurePipe.transform(FeatureName.Preference),
      this.featureFlagPurePipe.transform('operational-preferences'),
    ]).pipe(
      map(([hasPreferenceFeatureEnabled, hasPreferenceFeatureFlagEnabled]) => {
        return hasPreferenceFeatureEnabled && hasPreferenceFeatureFlagEnabled;
      }),
    );
  }

  triggerFetchForSubSettings(currentState: PreferenceSettingsStateModel) {
    if (currentState.isEnabled) {
      if (currentState.isLocationPrefEnabled) {
        this.store.dispatch(new FetchAndResolveLocationSettingsRemotely());
        this.store.dispatch(new FetchSelectedLocation());
      }
      if (currentState.isVehiclePrefEnabled) {
        this.store.dispatch(new FetchSelectedVehicles());
        this.store.dispatch(new FetchVehiclePreferenceSetting());
      }
      if (
        currentState.isWorkTimePrefEnabled ||
        currentState.isRestTimePrefEnabled
      ) {
        this.store.dispatch(new FetchUserTimePreferences());
      }
      if (
        currentState.isEndShiftAtHomeEnabled ||
        currentState.isLongShiftPrefEnabled
      ) {
        this.store.dispatch(new FetchUserGeneralPreferences());
      }
      if (currentState.isShiftTimePrefEnabled) {
        this.store.dispatch(new FetchShiftTimePreferenceSettingAction());
        this.store.dispatch(new FetchUserShiftTimePreferenceAction());
      }
    }
  }

  async updateSettingInStateAndServer(
    { patchState, getState }: StateContext<PreferenceSettingsStateModel>,
    newSettings: Partial<PreferenceSettings>,
    previousSettings: Partial<PreferenceSettings>,
  ) {
    patchState(newSettings);

    try {
      return await this.preferencesGatewayService.updateSetting(
        getState().id as string,
        newSettings,
      );
    } catch (e) {
      patchState(previousSettings);
      return Promise.reject(e);
    }
  }
}
